// @ts-nocheck
import { get, isEmpty, isFunction, isString, noop } from 'lodash'
import React, {
  CSSProperties,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { Modal, ModalProps } from 'react-bootstrap'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import Button from '../Button'
import LoaderOverlay from '../LoaderOverlay'

export { default as useDialog } from './useDialog'

const ButtonRow = styled.div`
  margin: -5px;
  display: flex;

  & > * {
    margin: 5px;
  }
`

let zIndex = 0
export interface IDialog extends ModalProps {
  /** Modal Title */
  title?: React.ReactNode

  /** Modal Title */
  header?: React.ReactNode

  /** Footer of the modal body */
  footer?: React.ReactNode

  /** when true The Modal will show an overlay loading */
  isLoading?: boolean

  /** When true The modal will show itself. */
  isOpen?: boolean

  /** A callback fired when the header closeButton or non-static backdrop is clicked. Required if either are specified. Only execute when isLoading is false */
  onClose?: () => void

  /** Set size of Dialog to small */
  small?: boolean

  /** Set size of Dialog to small */
  sm?: boolean

  /** Set size of Dialog to large */
  large?: boolean

  /** Set size of Dialog to large */
  lg?: boolean

  /** Set dialog width to full view width (95%)  */
  fullwidth?: boolean

  /** Set width to 75% of the view width  */
  width75?: boolean

  /** Submit button text */
  submitButton?: React.ReactNode

  /** Set props `type` to the submit button. Default `button` */
  submitButtonType?: 'submit' | 'reset' | 'button'

  /** Props for submit button */
  submitButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>

  /** Set button variant of submit button.  */
  submitButtonClass?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'success'
    | 'warning'
    | 'light'
    | 'dark'
    | 'info'

  /** When true, show modal footer */
  showFooter?: boolean

  /** Custom content of the modal footer */
  customFooter?: React.ReactElement

  /** A callback fired when submit button clicked */
  onSubmit?: () => void

  /** Submit button text */
  closeButton?: string | ReactElement

  /** Set cy-name props of the modal */
  name?: string

  /** When true, show close icon on upper right conner of the modal */
  showCloseIcon?: boolean
  onDelete?: () => void
  deleteButton?: React.ReactElement
  noHeader?: boolean
  bodyStyles?: CSSProperties
  width1000Pixels?: boolean
  backdrop?: boolean | 'static'
}

interface IParentNodeWithStyle extends ParentNode {
  style?: CSSProperties
}

interface IChildNodeWithStyle extends ChildNode {
  style?: CSSProperties
}
const Dialog = (props: IDialog): React.ReactElement => {
  const {
    title,
    header,
    children,
    footer,
    isLoading,
    isOpen,
    show,
    onHide = noop,
    onClose = noop,
    small,
    large,
    sm,
    lg,
    fullwidth,
    width75,
    submitButtonType = 'button',
    submitButtonProps = {},
    submitButton,
    submitButtonClass = 'primary',
    showFooter,
    onSubmit = noop,
    closeButton,
    name,
    showCloseIcon,
    customFooter,
    onDelete = noop,
    deleteButton,
    noHeader = false,
    bodyStyles = {},
    width1000Pixels,
    bsPrefix,
    centered,
    backdropClassName,
    animation,
    contentClassName,
    dialogAs,
    scrollable,
    backdrop,
  } = props

  const id = useMemo(() => `modal-${uuid()}`, [])
  const isVisible = useMemo(() => isOpen || show, [isOpen, show])

  const wasCounted = useRef(false)

  useEffect(() => {
    if (isVisible) {
      wasCounted.current = true
      zIndex++
      const modal: IParentNodeWithStyle | null | undefined =
        document.getElementById(id)?.parentNode
      if (modal) {
        const backdrop: IChildNodeWithStyle | null = get(
          modal,
          'previousSibling',
        )
        ;(modal.style as any).zIndex = 1050 + zIndex * 2
        if (backdrop) {
          ;(backdrop.style as any).zIndex = 1050 + zIndex * 2 - 1
        }
      }
    }
    return () => {
      if (isVisible) {
        if (wasCounted.current) {
          wasCounted.current = false
          zIndex--
        }
      }
    }
  }, [isVisible])

  let { size } = props

  if (sm || small) {
    size = 'sm'
  }
  if (lg || large) {
    size = 'lg'
  }

  let dialogClassName = props.dialogClassName || ''
  if (fullwidth) {
    dialogClassName = 'full-width-modal'
  }
  if (width75) {
    dialogClassName = 'almost-full-width-modal'
  }

  if (width1000Pixels) {
    dialogClassName = 'modal-width-1000px'
  }

  const close = (): void => {
    if (isLoading) {
      return
    }
    if (isFunction(onHide)) {
      onHide()
    }
    if (isFunction(onClose)) {
      onClose()
    }
  }

  return (
    <Modal
      id={id}
      show={isVisible}
      onHide={close}
      bsPrefix={bsPrefix}
      centered={centered}
      backdropClassName={backdropClassName}
      animation={animation}
      contentClassName={contentClassName}
      dialogAs={dialogAs}
      scrollable={scrollable}
      size={size}
      enforceFocus={false}
      dialogClassName={dialogClassName}
      cy-name={name}
      backdrop={backdrop}
    >
      <LoaderOverlay isLoading={isLoading} offset={0} />

      {!noHeader && (
        <Modal.Header closeButton={!isEmpty(closeButton) || showCloseIcon}>
          {title && <h4 style={{ margin: '0' }}>{title}</h4>}
          {header}
        </Modal.Header>
      )}

      <Modal.Body style={{ ...bodyStyles }}>
        {children}
        {footer && <div style={{}}>{footer}</div>}
      </Modal.Body>

      {(showFooter || closeButton || submitButton || customFooter) && (
        <Modal.Footer>
          <div className="flex-fill d-flex justify-content-between">
            <div>
              {React.isValidElement(deleteButton) && deleteButton}
              {isString(deleteButton) && (
                <Button danger onClick={onDelete}>
                  {deleteButton}
                </Button>
              )}
            </div>
            <ButtonRow>
              {React.isValidElement(closeButton) && closeButton}
              {isString(closeButton) && (
                <button
                  type="button"
                  className="btn btn-light waves-effect waves-light"
                  onClick={close}
                >
                  {closeButton}
                </button>
              )}
              {React.isValidElement(submitButton) && (
                <div className="d-inline">
                  {React.cloneElement(submitButton, {
                    onClick: onSubmit,
                  } as any)}
                </div>
              )}
              {isString(submitButton) && (
                <button
                  {...submitButtonProps}
                  type={submitButtonType}
                  className={`btn btn-${submitButtonClass} waves-effect waves-light`}
                  onClick={onSubmit}
                  cy-name="submit"
                >
                  {submitButton}
                </button>
              )}
            </ButtonRow>
          </div>

          {customFooter}
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default Dialog
